/**
 * Scan model loading for the CRM viewer (three.js, no React), so a test page
 * can run it as-is against a mock of the scan file proxy.
 */
import * as THREE from "three";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { MTLLoader } from "three/examples/jsm/loaders/MTLLoader.js";
import { PLYLoader } from "three/examples/jsm/loaders/PLYLoader.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import {
  fetchScanFileArrayBuffer,
  fetchScanFileBlobUrl,
  fetchScanFileImageBitmap,
  fetchScanFileImageBuffer,
  fetchScanFileText,
} from "./scanViewerApi";
import { decodeImageBitmap, imageDimensions } from "./imageDecode";
import { UNLIT_PHOTO_MATERIALS, applyUnlitPhotoMaterials } from "./scanViewerMaterials";

/**
 * GPU memory for a multi-atlas model's textures. An RGBA texture with mipmaps
 * costs width × height × 4 × 4/3 bytes. The decoded bitmap is released once
 * three has uploaded it (`releaseUploadedBitmap`), so the tab does not hold a
 * second copy.
 * - Full resolution while all atlases fit in 512 MiB: up to 6 × 4096², which
 *   covers 27 of the 50 fleet scans (run 7s).
 * - Otherwise ≤ 2048 px, the size the phone itself displays
 *   (atlas_k_preview.jpg); lower would look softer than the phone. Fleet:
 *   7–15 atlases of 4096 → 149–320 MiB; the one coarsened 32 × 2048 run stays
 *   at 683 MiB.
 * - ≤ 1024 px only if 2048 would still need more than 1 GiB (no fleet scan).
 */
export const ATLAS_TEXTURE_BUDGET_BYTES = 512 * 1024 * 1024;
export const ATLAS_TEXTURE_HARD_CAP_BYTES = 1024 * 1024 * 1024;
const ATLAS_MAX_BYTES = 48 * 1024 * 1024;
const ATLAS_FETCH_CONCURRENCY = 4;
// createImageBitmap decodes off the main thread; 3 at a time roughly halves
// the wait for a 4096 → 2048 "high" resize without holding many full-size
// decodes at once.
const ATLAS_DECODE_CONCURRENCY = 3;

const PREVIEW_PICK_HINT = `Pick "On-device preview (textured mesh)" above.`;

function abortError() {
  return new DOMException("Model load cancelled", "AbortError");
}

function throwIfAborted(signal) {
  if (signal?.aborted) throw abortError();
}

export function isAbortError(err) {
  return err?.name === "AbortError";
}

function texturesGpuBytes(sizes, maxEdge) {
  return sizes.reduce((sum, { width, height }) => {
    const longest = Math.max(width, height);
    const scale = maxEdge && longest > maxEdge ? maxEdge / longest : 1;
    return sum + Math.round(width * scale) * Math.round(height * scale) * 4 * (4 / 3);
  }, 0);
}

/** Decode edge for a run's atlases (0 = full resolution); see the budget above. */
export function atlasDecodeEdge(sizes) {
  if (texturesGpuBytes(sizes, 0) <= ATLAS_TEXTURE_BUDGET_BYTES) return 0;
  if (texturesGpuBytes(sizes, 2048) <= ATLAS_TEXTURE_HARD_CAP_BYTES) return 2048;
  return 1024;
}

function applyTextureToMeshes(object, texture) {
  object.traverse((child) => {
    if (!child.isMesh) return;
    if (!child.geometry.attributes.normal) {
      child.geometry.computeVertexNormals();
    }

    const sourceMaterials = Array.isArray(child.material)
      ? child.material
      : child.material
        ? [child.material]
        : [];

    if (!sourceMaterials.length || sourceMaterials.every((mat) => !mat)) {
      child.material = new THREE.MeshStandardMaterial({
        map: texture,
        color: 0xffffff,
        roughness: 0.6,
        metalness: 0.1,
        side: THREE.DoubleSide,
      });
      return;
    }

    child.material = sourceMaterials.map((mat) => {
      if (!mat) {
        return new THREE.MeshStandardMaterial({
          map: texture,
          color: 0xffffff,
          roughness: 0.6,
          metalness: 0.1,
          side: THREE.DoubleSide,
        });
      }

      const next = new THREE.MeshStandardMaterial({
        map: texture,
        color: 0xffffff,
        roughness: mat.roughness ?? 0.6,
        metalness: mat.metalness ?? 0.1,
        transparent: Boolean(mat.transparent),
        opacity: mat.opacity ?? 1,
        side: THREE.DoubleSide,
      });
      if (mat.map && mat.map !== texture) mat.map.dispose?.();
      mat.dispose?.();
      return next;
    });
    if (child.material.length === 1) child.material = child.material[0];
  });
}

function textureLoadOptions(viewer, zipBundle, { extract } = {}) {
  const filename = viewer?.texture_filename || "";
  const lower = filename.toLowerCase();
  const extractKey = extract || (lower.endsWith(".png") ? "png" : "jpg");
  const zipTexture = Boolean(
    viewer?.texture_from_zip || zipBundle || lower.endsWith(".zip") || extract
  );
  return {
    ...(zipTexture ? { extract: extractKey } : {}),
    filename,
    mimeType: extractKey === "png" ? "image/png" : "image/jpeg",
  };
}

async function loadPreviewTexture(scanId, viewer, zipBundle, signal) {
  const meshId = viewer?.mesh_file_id;
  const attempts = [];
  const seen = new Set();

  const addAttempt = (fileId, opts) => {
    const key = `${fileId}:${opts.extract || "direct"}`;
    if (!fileId || seen.has(key)) return;
    seen.add(key);
    attempts.push({ fileId, opts });
  };

  // The on-device preview package has its own OBJ/MTL/JPG bundle. Prefer its
  // standalone texture; the processed model ZIP is only a fallback.
  if (
    viewer?.texture_file_id &&
    viewer.texture_file_id !== meshId &&
    !viewer.texture_from_zip
  ) {
    addAttempt(
      viewer.texture_file_id,
      textureLoadOptions(viewer, false)
    );
  }

  if (viewer?.texture_file_id && (viewer.texture_from_zip || zipBundle)) {
    addAttempt(
      viewer.texture_file_id,
      textureLoadOptions(viewer, true, { extract: "jpg" })
    );
    addAttempt(
      viewer.texture_file_id,
      textureLoadOptions(viewer, true, { extract: "png" })
    );
  }

  if (viewer?.obj_zip_file_id) {
    addAttempt(
      viewer.obj_zip_file_id,
      textureLoadOptions(viewer, true, { extract: "jpg" })
    );
    addAttempt(
      viewer.obj_zip_file_id,
      textureLoadOptions(viewer, true, { extract: "png" })
    );
  }

  for (const { fileId, opts } of attempts) {
    throwIfAborted(signal);
    try {
      return await loadTextureFromProxy(scanId, fileId, { ...opts, signal });
    } catch (err) {
      if (isAbortError(err)) throw err;
      /* try next source */
    }
  }
  return null;
}

/**
 * Texture.onUpdate: three has copied the bitmap to the GPU, so the decoded copy
 * in the tab is no longer needed. Up to 512 MiB for a 32-atlas model. A lost
 * WebGL context is therefore not restored in place: the viewer asks to reopen.
 */
function releaseUploadedBitmap(texture) {
  texture.onUpdate = null;
  texture.image?.close?.();
}

function textureFromBitmap(bitmap) {
  const texture = new THREE.Texture(bitmap);
  texture.colorSpace = THREE.SRGBColorSpace;
  // The vertical flip is baked into the ImageBitmap during decode.
  texture.flipY = false;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.onUpdate = releaseUploadedBitmap;
  texture.needsUpdate = true;
  return texture;
}

async function loadTextureFromProxy(scanId, textureFileId, options = {}) {
  const bitmap = await fetchScanFileImageBitmap(scanId, textureFileId, options);
  return textureFromBitmap(bitmap);
}

async function loadTextureFromUrl(url) {
  const loader = new THREE.TextureLoader();
  const texture = await new Promise((resolve, reject) => {
    loader.load(url, resolve, undefined, reject);
  });
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.flipY = true;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  return texture;
}

function meshFormatFromViewer(viewer) {
  if (viewer?.mesh_format) return viewer.mesh_format;
  const name = String(viewer?.mesh_filename || "").toLowerCase();
  if (name.endsWith(".glb") || name.endsWith(".gltf")) return "glb";
  if (name.endsWith(".usdz")) return "usdz";
  if (name.endsWith(".ply")) return "ply";
  if (name.endsWith(".zip")) return "zip_obj";
  return "obj";
}

async function fetchAsset(url, { accept, signal } = {}) {
  const res = await fetch(url, {
    mode: "cors",
    credentials: "omit",
    headers: accept ? { Accept: accept } : undefined,
    signal,
  });
  if (!res.ok) {
    throw new Error(`Failed to load file (${res.status})`);
  }
  return res;
}

async function loadGlbFromUrl(url, signal) {
  const res = await fetchAsset(url, { accept: "model/gltf-binary,model/gltf+json,*/*", signal });
  const buffer = await res.arrayBuffer();
  throwIfAborted(signal);
  const loader = new GLTFLoader();
  return new Promise((resolve, reject) => {
    loader.parse(buffer, "", (gltf) => resolve(gltf.scene), reject);
  });
}

/** `element face N` from a PLY header (null when the header cannot be read). */
export function plyHeaderFaceCount(buffer) {
  if (!(buffer instanceof ArrayBuffer)) return null;
  const head = new TextDecoder().decode(new Uint8Array(buffer, 0, Math.min(buffer.byteLength, 64 * 1024)));
  const end = head.indexOf("end_header");
  if (!head.startsWith("ply") || end < 0) return null;
  const match = /^element\s+face\s+(\d+)\s*$/m.exec(head.slice(0, end));
  return match ? Number(match[1]) : 0;
}

/**
 * PLY → scene object. The device PLYs (device_preview.ply, recon.ply) carry
 * per-vertex uchar red/green/blue; PLYLoader decodes them as sRGB into the
 * linear working space, so the material base colour must be white for the
 * vertex colours to show as captured. Faces render as a mesh, face-less files
 * as points. PLYLoader un-indexes a mesh whose faces carry colours or
 * texcoords, so `faceCount` (from the header) keeps such a file a mesh.
 */
export function plyObjectFromGeometry(geometry, { faceCount = null } = {}) {
  const hasColor = geometry.hasAttribute("color");
  const object = new THREE.Group();
  const position = geometry.getAttribute("position");
  const indexed = Boolean(geometry.index && geometry.index.count > 0);
  const unrolledFaces =
    !indexed && faceCount > 0 && position && position.count >= 3 && position.count % 3 === 0;
  if (!indexed && !unrolledFaces) {
    object.add(
      new THREE.Points(
        geometry,
        new THREE.PointsMaterial({
          size: 0.02,
          vertexColors: hasColor,
          color: hasColor ? 0xffffff : 0xcccccc,
          sizeAttenuation: true,
        })
      )
    );
    return object;
  }
  if (!geometry.hasAttribute("normal")) geometry.computeVertexNormals();
  object.add(
    new THREE.Mesh(
      geometry,
      new THREE.MeshStandardMaterial({
        color: hasColor ? 0xffffff : 0xcccccc,
        vertexColors: hasColor,
        roughness: 0.6,
        metalness: 0.1,
        side: THREE.DoubleSide,
      })
    )
  );
  return object;
}

function plyObjectFromBuffer(buffer) {
  return plyObjectFromGeometry(new PLYLoader().parse(buffer), { faceCount: plyHeaderFaceCount(buffer) });
}

async function loadPlyFromUrl(url, signal) {
  const res = await fetchAsset(url, { accept: "application/octet-stream,*/*", signal });
  const buffer = await res.arrayBuffer();
  throwIfAborted(signal);
  return plyObjectFromBuffer(buffer);
}

function objectVertexCount(object) {
  let count = 0;
  object.traverse((child) => {
    count += child.geometry?.getAttribute?.("position")?.count || 0;
  });
  return count;
}

function formatMegabytes(bytes) {
  return `${Math.round(bytes / 1e6)} MB`;
}

/**
 * OBJ text → object. An OBJ larger than the browser's longest string comes
 * back from Response.text() as "" without an error; an empty result is
 * reported instead of an empty canvas.
 */
function parseObjText(meshText, { materials = null, filename = "", fileSize = null } = {}) {
  if (!meshText) {
    const size = fileSize ? ` (${formatMegabytes(fileSize)})` : "";
    throw new Error(
      `${filename || "The model file"}${size} could not be read in the browser: it is empty or too large. ` +
        `Pick another package above.`
    );
  }
  const loader = new OBJLoader();
  if (materials) loader.setMaterials(materials);
  const object = loader.parse(meshText);
  if (!objectVertexCount(object)) {
    throw new Error(`${filename || "The model file"} has no geometry. Pick another package above.`);
  }
  return object;
}

async function loadObjFromUrls({ meshUrl, mtlUrl, textureUrl, filename, signal }) {
  let mtlMaterials = null;
  if (mtlUrl) {
    try {
      const mtlRes = await fetchAsset(mtlUrl, { signal });
      const mtlText = await mtlRes.text();
      const mtlLoader = new MTLLoader();
      mtlMaterials = mtlLoader.parse(mtlText, "");
      mtlMaterials.preload();
    } catch (err) {
      if (isAbortError(err)) throw err;
      mtlMaterials = null;
    }
  }

  let texture = null;
  if (textureUrl) {
    try {
      texture = await loadTextureFromUrl(textureUrl);
    } catch (_) {
      texture = null;
    }
  }
  throwIfAborted(signal);

  const meshRes = await fetchAsset(meshUrl, { accept: "model/obj,*/*", signal });
  const meshText = await meshRes.text();
  throwIfAborted(signal);
  const object = parseObjText(meshText, {
    materials: mtlMaterials?.materials ? mtlMaterials : null,
    filename,
  });
  if (texture) applyTextureToMeshes(object, texture);
  return object;
}

function textureBasename(value) {
  return String(value || "")
    .trim()
    .split(/\s+/)
    .pop()
    .split(/[/\\]/)
    .pop()
    .toLowerCase();
}

async function withRetry(task, { attempts = 2, signal } = {}) {
  let lastError = null;
  for (let i = 0; i < attempts; i += 1) {
    throwIfAborted(signal);
    try {
      return await task();
    } catch (err) {
      if (isAbortError(err)) throw err;
      lastError = err;
    }
  }
  throw lastError;
}

async function mapWithConcurrency(items, limit, worker, signal) {
  const results = new Array(items.length);
  let next = 0;
  const run = async () => {
    while (next < items.length) {
      throwIfAborted(signal);
      const index = next;
      next += 1;
      results[index] = await worker(items[index], index);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, run));
  return results;
}

/**
 * On-device ReconKit model (recon.obj + recon.mtl + atlas_k.jpg): one material
 * per atlas, each atlas fetched by its own file id through the proxy and set
 * on its own material. Never a single-texture override. A missing or
 * undecodable atlas fails the load instead of leaving a grey material.
 */
async function loadMultiMaterialObj({ scanId, viewer, onStep, signal }) {
  const textureFiles = {};
  for (const [name, fileId] of Object.entries(viewer.texture_files || {})) {
    textureFiles[textureBasename(name)] = fileId;
  }

  onStep?.("Fetching materials…");
  // The OBJ is the largest download; fetch it while the atlases load.
  const meshTextPromise = fetchScanFileText(scanId, viewer.mesh_file_id, { signal });
  meshTextPromise.catch(() => {});

  const mtlText = await fetchScanFileText(scanId, viewer.mtl_file_id, { signal });
  const creator = new MTLLoader()
    .setMaterialOptions({ side: THREE.DoubleSide })
    .parse(mtlText, "");
  // MTLLoader would fetch map_Kd relative to the CRM origin; take the names
  // out before preload() and set the proxied textures afterwards.
  const mapByMaterial = {};
  for (const [materialName, info] of Object.entries(creator.materialsInfo)) {
    for (const key of Object.keys(info)) {
      if (key === "map_kd") mapByMaterial[materialName] = textureBasename(info[key]);
      if (key.startsWith("map_") || key === "bump" || key === "disp" || key === "norm") {
        delete info[key];
      }
    }
  }
  creator.preload();

  const names = Array.from(new Set(Object.values(mapByMaterial)));
  const missing = names.filter((name) => !textureFiles[name]);
  if (!names.length || missing.length) {
    throw new Error(
      `recon.mtl names ${missing.join(", ") || "no textures"}, which this upload does not include. ` +
        PREVIEW_PICK_HINT
    );
  }

  let fetched = 0;
  onStep?.(`Loading textures 0/${names.length}…`);
  const buffers = await mapWithConcurrency(
    names,
    ATLAS_FETCH_CONCURRENCY,
    async (name) => {
      try {
        const buffer = await withRetry(
          () => fetchScanFileImageBuffer(scanId, textureFiles[name], { signal }),
          { signal }
        );
        fetched += 1;
        onStep?.(`Loading textures ${fetched}/${names.length}…`);
        return buffer;
      } catch (err) {
        if (isAbortError(err)) throw err;
        throw new Error(`Could not load texture ${name} (${err?.message || err}). ${PREVIEW_PICK_HINT}`);
      }
    },
    signal
  );

  const sizes = buffers.map((buffer) => imageDimensions(buffer) || { width: 4096, height: 4096 });
  const maxEdge = atlasDecodeEdge(sizes);
  let prepared = 0;
  onStep?.(`Preparing textures 0/${names.length}…`);
  // Every decoded bitmap, so a failed or cancelled load closes them all.
  const opened = [];
  const closeAll = () => opened.forEach((bitmap) => bitmap.close());
  let decoded;
  try {
    decoded = await mapWithConcurrency(
      names,
      ATLAS_DECODE_CONCURRENCY,
      async (name, i) => {
        let bitmap;
        try {
          bitmap = await decodeImageBitmap(buffers[i], { maxEdge, maxBytes: ATLAS_MAX_BYTES });
        } catch (error) {
          return { error };
        }
        buffers[i] = null;
        if (signal?.aborted) {
          bitmap.close();
          throw abortError();
        }
        opened.push(bitmap);
        prepared += 1;
        onStep?.(`Preparing textures ${prepared}/${names.length}…`);
        return { bitmap };
      },
      signal
    );
  } catch (err) {
    closeAll();
    throw err;
  }
  const failed = decoded.findIndex((entry) => entry.error);
  if (failed >= 0) {
    closeAll();
    const err = decoded[failed].error;
    throw new Error(`Could not decode texture ${names[failed]} (${err?.message || err}). ${PREVIEW_PICK_HINT}`);
  }
  if (signal?.aborted) {
    closeAll();
    throw abortError();
  }
  const textures = {};
  names.forEach((name, i) => {
    textures[name] = textureFromBitmap(decoded[i].bitmap);
  });

  for (const [materialName, name] of Object.entries(mapByMaterial)) {
    const material = creator.materials[materialName];
    if (!material) continue;
    material.map = textures[name];
    material.needsUpdate = true;
  }

  let object;
  try {
    onStep?.("Fetching mesh file…");
    const meshText = await meshTextPromise;
    throwIfAborted(signal);
    onStep?.("Parsing 3D model…");
    object = parseObjText(meshText, {
      materials: creator,
      filename: viewer.mesh_filename,
      fileSize: viewer.mesh_file_size,
    });
  } catch (err) {
    // Not uploaded yet, so the bitmaps are still the only copies: close them.
    closeAll();
    throw err;
  }
  object.userData.textureEdge = maxEdge;
  object.userData.textureCount = names.length;
  return object;
}

async function loadFromProxy({ scanId, viewer, onStep, signal }) {
  const meshFileId = viewer.mesh_file_id;
  const meshFilename = viewer.mesh_filename;
  const format = meshFormatFromViewer(viewer);
  const zipBundle = format === "zip_obj";

  onStep?.("Fetching mesh file…");
  if (format === "glb") {
    onStep?.("Parsing GLB…");
    const buffer = await fetchScanFileArrayBuffer(scanId, meshFileId, { signal });
    throwIfAborted(signal);
    const loader = new GLTFLoader();
    const object = await new Promise((resolve, reject) => {
      loader.parse(buffer, "", (gltf) => resolve(gltf.scene), reject);
    });
    return { kind: "three", object, label: meshFilename };
  }
  if (format === "ply") {
    const buffer = await fetchScanFileArrayBuffer(scanId, meshFileId, { signal });
    throwIfAborted(signal);
    onStep?.("Parsing PLY…");
    const object = plyObjectFromBuffer(buffer);
    return { kind: "three", object, label: meshFilename };
  }
  if (format === "usdz") {
    const blobUrl = await fetchScanFileBlobUrl(scanId, meshFileId, { signal });
    return { kind: "usdz", blobUrl, label: meshFilename };
  }

  if (!zipBundle && viewer.mtl_file_id && viewer.texture_files && Object.keys(viewer.texture_files).length) {
    const object = await loadMultiMaterialObj({ scanId, viewer, onStep, signal });
    return { kind: "three", object, label: meshFilename };
  }

  onStep?.("Parsing 3D model…");
  const meshText = await fetchScanFileText(
    scanId,
    meshFileId,
    zipBundle ? { extract: "obj", signal } : { signal }
  );
  const mtlText = viewer.mtl_file_id
    ? await fetchScanFileText(scanId, viewer.mtl_file_id, zipBundle ? { extract: "mtl", signal } : { signal })
    : null;
  let texture = null;
  if (viewer.texture_file_id || viewer.obj_zip_file_id) {
    try {
      texture = await loadPreviewTexture(scanId, viewer, zipBundle, signal);
    } catch (err) {
      if (isAbortError(err)) throw err;
      console.warn("CRM scan viewer: texture load failed", err);
      texture = null;
    }
    if (!texture) {
      console.warn("CRM scan viewer: no decodable preview texture found");
    }
  }
  if (signal?.aborted) {
    texture?.image?.close?.();
    throw abortError();
  }
  let materials = null;
  if (mtlText) {
    materials = new MTLLoader().parse(mtlText, "");
    materials.preload();
  }
  let object;
  try {
    object = parseObjText(meshText, { materials, filename: meshFilename, fileSize: viewer.mesh_file_size });
  } catch (err) {
    texture?.image?.close?.();
    throw err;
  }
  if (texture) applyTextureToMeshes(object, texture);
  return { kind: "three", object, label: meshFilename };
}

/**
 * Loads the model a viewer descriptor points at. `signal` (AbortSignal) stops
 * the downloads and decodes of a load the viewer no longer wants (modal
 * closed, another package picked); the promise then rejects with an
 * AbortError (`isAbortError`). Textured and vertex-coloured meshes come back
 * unlit (scanViewerMaterials.js).
 */
export async function loadModelGroup(options) {
  const loaded = await loadModelGroupMaterials(options);
  if (loaded?.kind === "three" && UNLIT_PHOTO_MATERIALS) applyUnlitPhotoMaterials(loaded.object);
  return loaded;
}

async function loadModelGroupMaterials({ scanId, viewer, onStep, signal }) {
  throwIfAborted(signal);
  if (viewer.mesh_file_id) {
    return loadFromProxy({ scanId, viewer, onStep, signal });
  }
  if (viewer.mesh_url) {
    const format = meshFormatFromViewer(viewer);
    onStep?.("Loading from storage…");
    if (format === "usdz") {
      return { kind: "usdz", blobUrl: viewer.mesh_url, label: viewer.mesh_filename };
    }
    if (format === "glb") {
      onStep?.("Parsing GLB…");
      const object = await loadGlbFromUrl(viewer.mesh_url, signal);
      return { kind: "three", object, label: viewer.mesh_filename };
    }
    if (format === "ply") {
      onStep?.("Parsing PLY…");
      const object = await loadPlyFromUrl(viewer.mesh_url, signal);
      return { kind: "three", object, label: viewer.mesh_filename };
    }
    onStep?.("Parsing OBJ…");
    const object = await loadObjFromUrls({
      meshUrl: viewer.mesh_url,
      mtlUrl: viewer.mtl_url,
      textureUrl: viewer.texture_url,
      filename: viewer.mesh_filename,
      signal,
    });
    return { kind: "three", object, label: viewer.mesh_filename };
  }

  return loadFromProxy({ scanId, viewer, onStep, signal });
}

/** Frees geometry, materials, textures and decoded bitmaps of a loaded object. */
export function disposeObject3D(root) {
  root?.traverse((child) => {
    if (child.geometry) child.geometry.dispose();
    if (child.material) {
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      mats.forEach((mat) => {
        if (mat.map) {
          mat.map.dispose();
          mat.map.image?.close?.();
        }
        mat.dispose();
      });
    }
  });
}
